import { useState } from 'react';

import type { RowBackground } from '@noti/shared';

import { t } from '../i18n';
import { ROW_BACKGROUNDS } from './database-rows';
import type { DatabaseRow } from './database-rows';
import { ActionMenu, ActionMenuItem, ActionMenuInput } from './ActionMenu';
import { blockBackgroundVar } from './extensions';

export type RowAction = 'above' | 'below' | 'duplicate' | 'clear' | 'delete';

export const SWATCH_LINE = 'var(--color-ink)';

interface DatabaseRowMenuProps {
  row: DatabaseRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAction: (rowId: string, action: RowAction) => void;
  onColor: (rowId: string, color: RowBackground | null) => void;
  onOpen: (rowId: string) => void;
}

interface RowMenuItem {
  key: 'open' | RowAction;
  glyph: string;
  label: string;
  hint: string;
}

export function DatabaseRowMenu({
  row,
  open,
  onOpenChange,
  onAction,
  onColor,
  onOpen,
}: DatabaseRowMenuProps): React.ReactElement {
  const [colorOpen, setColorOpen] = useState(false);
  const [query, setQuery] = useState('');

  const actions: RowMenuItem[] = [
    { key: 'open', glyph: '↗', label: t('database.openAsPage'), hint: '' },
    { key: 'above', glyph: '↑', label: t('database.insertAbove'), hint: '' },
    { key: 'below', glyph: '↓', label: t('database.insertBelow'), hint: '' },
    { key: 'duplicate', glyph: '❐', label: t('database.duplicate'), hint: '⌘D' },
    { key: 'clear', glyph: '×', label: t('database.clearContents'), hint: '' },
    { key: 'delete', glyph: '✕', label: t('database.delete'), hint: '' },
  ];
  const items = actions.filter(
    (item) => query === '' || item.label.toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <ActionMenu
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setQuery('');
          setColorOpen(false);
        }
        onOpenChange(next);
      }}
      label={t('database.columnOptions')}
      className="menu--row"
      align="start"
      trigger={
        <button
          type="button"
          className="eg eg--v"
          aria-label={t('database.columnOptions')}
          title={t('database.columnOptionsTitle')}
        >
          <span className="eg__gb">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <circle cx="9" cy="6" r="1.8" />
              <circle cx="15" cy="6" r="1.8" />
              <circle cx="9" cy="12" r="1.8" />
              <circle cx="15" cy="12" r="1.8" />
              <circle cx="9" cy="18" r="1.8" />
              <circle cx="15" cy="18" r="1.8" />
            </svg>
          </span>
        </button>
      }
    >
      <ActionMenuInput
        className="ni gmenu__search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={t('database.searchActions')}
        aria-label={t('database.searchActionsLabel')}
      />
      {items.map((item) => (
        <ActionMenuItem
          key={item.key}
          danger={item.key === 'delete'}
          onSelect={() => (item.key === 'open' ? onOpen(row.id) : onAction(row.id, item.key))}
        >
          <span className="gmenu__glyph">{item.glyph}</span>
          <span className="gmenu__label">{item.label}</span>
          {item.hint ? <span className="gmenu__hint">{item.hint}</span> : null}
        </ActionMenuItem>
      ))}
      <ActionMenuItem
        keepOpen
        className="menu__item nb gmenu__item--split"
        aria-expanded={colorOpen}
        onSelect={() => setColorOpen((value) => !value)}
      >
        <span className="gmenu__left">
          <span className="gmenu__glyph">▤</span>
          {t('database.color')}
        </span>
        <span className="gmenu__hint">›</span>
      </ActionMenuItem>
      {colorOpen ? (
        <div className="gmenu__swatches">
          {ROW_BACKGROUNDS.map((background) => (
            <ActionMenuItem
              key={background.label}
              keepOpen
              className="gmenu__swatch nb"
              title={background.label}
              aria-label={background.label}
              style={{
                background: blockBackgroundVar(background.value) ?? 'var(--color-surface)',
                borderColor: row.color === background.value ? 'var(--color-primary)' : SWATCH_LINE,
              }}
              onSelect={() => onColor(row.id, background.value)}
            />
          ))}
        </div>
      ) : null}
      <div className="gmenu__foot">
        {row.title ? `Last edited ${row.editedAt.slice(0, 10)}` : 'Last edited by you'}
      </div>
    </ActionMenu>
  );
}
